import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from 'oidc-client-ts';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { mapJsonObjectToClient } from '../services';
import { I6ApiResponse } from './i6-api-response';
import { RoleTypes } from './role-types';

export interface ConfirmAccountRequest {
  userId: string;
  token: string;
}

export interface SignUpResponse {
  newUser: boolean;
  message: string;
  invalid: boolean;
}

export interface ResetPasswordRequest extends ConfirmAccountRequest {
  newPassword: string;
}

export interface ResetPasswordLinkResponse {
  resetPasswordLink: string;
  message: string;
  invalid: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _authenticated: boolean = false;
  private _authMode: 'legacy' | 'aryza' = (localStorage.getItem('authMode') as 'legacy' | 'aryza') ?? 'legacy';
  private headlessToken: string;
  private silentSignIdId: number;
  private accessTokenSubject = new BehaviorSubject<string>(this.accessToken);

  /**
   * Constructor
   */
  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    if (this.isHeadless) {
      this.headlessToken = token;
    } else if (!token) {
      localStorage.removeItem('accessToken');
    } else {
      localStorage.setItem('accessToken', token);
    }
    this.accessTokenSubject.next(this.accessToken);
  }

  get accessToken(): string {
    return (this.isHeadless ? this.headlessToken : localStorage.getItem('accessToken')) ?? undefined;
  }

  get accessToken$(): Observable<string> {
    return this.accessTokenSubject.asObservable();
  }

  get isAryzaAuth(): boolean {
    return this._authMode === 'aryza';
  }

  set refreshToken(token: string) {
    if (!token) {
      localStorage.removeItem('refreshToken');
    } else {
      localStorage.setItem('refreshToken', token);
    }
  }

  get refreshToken(): string {
    return localStorage.getItem('refreshToken') ?? undefined;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    return this._httpClient.post(`${environment.apiRoot}/Auth/ForgotPassword`, { email: email });
  }

  /**
   * Reset password
   *
   * @param password
   */
  resetPassword(request: ResetPasswordRequest): Observable<any> {
    return this._httpClient.post(`${environment.apiRoot}/Auth/ResetPassword`, request);
  }

  /**
   * Reset password
   *
   * @param password
   */
  resetPasswordv2(request: ResetPasswordRequest): Observable<I6ApiResponse> {
    return this._httpClient
      .post<I6ApiResponse>(`${environment.apiRoot}/Auth/ResetPasswordv2`, request)
      .pipe(map((response) => mapJsonObjectToClient(response)));
  }

  /**
   * Confirm account
   */
  confirmAccount(request: ConfirmAccountRequest): Observable<ResetPasswordLinkResponse> {
    return this._httpClient
      .post<ResetPasswordLinkResponse>(`${environment.apiRoot}/Auth/ConfirmAccount`, request)
      .pipe(map((response) => mapJsonObjectToClient(response)));
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { email: string; password: string; rememberMe: boolean }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError(() => new Error('User is already logged in.'));
    }

    return this._httpClient
      .post(`${environment.apiRoot}/Token`, {
        email: credentials.email,
        password: credentials.password,
        rememberMe: credentials.rememberMe,
      })
      .pipe(
        switchMap((response: any) => {
          response = mapJsonObjectToClient(response);
          // Store the access tokens in the local storage
          this.accessToken = response.accessToken;
          this.refreshToken = response.refreshToken;

          // Normal (legacy) login — reset any stale Aryza/SSO auth mode so the token-refresh
          // handling (check/silentSignIn) treats this as a legacy session.
          localStorage.removeItem('authMode');
          this._authMode = 'legacy';

          // Set the authenticated flag to true
          this._authenticated = true;

          // Setup silent token refresh
          this.silentSignIn();

          // Return a new observable with the response
          return of(response);
        }),
      );
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    const request = this.isHeadless
      ? of({ accessToken: this.accessToken })
      : this._httpClient.post(`${environment.apiRoot}/Refresh`, {
          accessToken: this.accessToken,
          refreshToken: this.refreshToken,
        });
    // Renew token
    return request.pipe(
      catchError(() =>
        // Return false
        of(false),
      ),
      switchMap((response: any) => {
        response = mapJsonObjectToClient(response);

        // Store the access token in the local storage
        this.accessToken = response.accessToken;
        this.refreshToken = response.refreshToken;

        // Set the authenticated flag to true
        this._authenticated = true;

        // Setup silent token refresh
        this.silentSignIn();

        // Return true
        return of(true);
      }),
    );
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    this.clearTokens();
    return of(true);
  }

  /**
   * Exchange an Aryza opaque access token for an i6api JWT via the /AryzaToken endpoint.
   * After this, the app is in the same state as a normal legacy login.
   * authMode is set to 'aryza' only to drive the correct sign-out flow (Aryza endsession).
   */
  exchangeAryzaToken(aryzaAccessToken: string, aryzaIdToken: string): Observable<any> {
    return this._httpClient
      .post(`${environment.apiRoot}/AryzaToken`, { aryzaToken: aryzaAccessToken }, {
        headers: { Authorization: `Bearer ${aryzaIdToken}` },
      })
      .pipe(
        switchMap((response: any) => {
          response = mapJsonObjectToClient(response);
          this.accessToken = response.accessToken;
          this.refreshToken = response.refreshToken;
          // Store id_token and authMode only for the Aryza sign-out redirect
          localStorage.setItem('aryzaIdToken', aryzaIdToken);
          localStorage.setItem('authMode', 'aryza');
          this._authMode = 'aryza';
          this._authenticated = true;
          this.silentSignIn();
          return of(response);
        }),
      );
  }

  completeAryzaSignIn(user: User): void {
    this.accessToken = user.access_token;
    this.refreshToken = user.refresh_token ?? '';
    localStorage.setItem('aryzaIdToken', user.id_token ?? '');
    localStorage.setItem('authMode', 'aryza');
    this._authMode = 'aryza';
    this._authenticated = true;

    // Extract roles from the ID token profile (ID token IS a JWT, unlike the opaque access token)
    const profile = user.profile as Record<string, unknown>;
    const raw = profile?.['roles'] ?? profile?.['role'];
    const roles: string[] = raw ? (Array.isArray(raw) ? (raw as string[]) : [raw as string]) : [];
    localStorage.setItem('aryzaRoles', JSON.stringify(roles));
  }

  aryzaSignOut(): void {
    const idToken = localStorage.getItem('aryzaIdToken') ?? '';
    const postLogoutUri = encodeURIComponent(`${window.location.origin}/sign-out`);
    this.clearTokens();
    localStorage.removeItem('aryzaIdToken');
    localStorage.removeItem('authMode');
    localStorage.removeItem('aryzaRoles');
    this._authMode = 'legacy';
    window.location.href = `${environment.aryzaAuthUrl}/connect/endsession?id_token_hint=${idToken}&post_logout_redirect_uri=${postLogoutUri}`;
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: { name: string; email: string; password: string; company: string }): Observable<SignUpResponse> {
    return this._httpClient
      .post<SignUpResponse>(`${environment.apiRoot}/Auth/SignUp`, user)
      .pipe(map((response) => mapJsonObjectToClient(response)));
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this._httpClient.post('api/auth/unlock-session', credentials);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check the access token availability
    if (!this.accessToken) {
      this.clearTokens();
      return of(false);
    }

    // After the /AryzaToken exchange the stored token is a normal i6api JWT, so we can
    // validate its expiry just like legacy. If it's expired we must force re-authentication
    // (the AuthGuard will redirect to sign-in) instead of letting protected calls 401 and
    // leave the app stuck on the loading splash.
    if (this._authMode === 'aryza') {
      if (!AuthUtils.isTokenExpired(this.accessToken)) {
        this._authenticated = true;
        return of(true);
      }
      this.clearTokens();
      return of(false);
    }

    // Check if the user is logged in and token still valid
    if (this._authenticated && !AuthUtils.isTokenExpired(this.accessToken)) {
      return of(true);
    }

    // Check the refresh token availability
    if (!this.refreshToken) {
      this.clearTokens();
      return of(false);
    }

    // If the access and refresh tokens exist, sign in using it
    return this.signInUsingToken().pipe(
      map((authenticated) => {
        if (!authenticated) {
          this.clearTokens();
        }

        return authenticated;
      }),
    );
  }

  /**
   * Check if user has the provided role
   */
  hasRole(role: string): boolean {
    const roles = AuthUtils.getRoles(this.accessToken).map((r) => r.toLowerCase());
    return roles.indexOf(role?.toLowerCase()) !== -1;
  }

  isTimekeepingAdmin(): boolean {
    return this.hasRole(RoleTypes.timekeepingAdmin);
  }

  isInsolSaasAdmin(): boolean {
    return this.hasRole(RoleTypes.insolsaasAdmin);
  }

  /**
   * Get practiceuserid if user is connected to a practice database
   */
  getPracticeUserId(): number {
    return AuthUtils.getPracticeUserId(this.accessToken);
  }

  getPracticeId(): number {
    return AuthUtils.getPracticeId(this.accessToken);
  }

  getPracticeUserName(): string {
    return AuthUtils.getUsername(this.accessToken);
  }

  private get isHeadless(): boolean {
    return navigator.userAgent.indexOf('HeadlessChrome') !== -1;
  }

  private silentSignIn(): void {
    if (this._authMode === 'aryza') {
      return; // Aryza tokens are opaque — cannot compute timeout client-side
    }

    if (this.silentSignIdId) {
      clearTimeout(this.silentSignIdId);
    }

    const token = this.accessToken;

    // Token has to be valid for silent login
    // if (AuthUtils.isTokenExpired(token)) {
    //   return;
    // }

    const tokenTimeout = AuthUtils.getTokenTimeout(this.accessToken, 60);
    if (tokenTimeout) {
      this.silentSignIdId = +setTimeout(() => {
        // Check if the user is logged in
        if (!this._authenticated) {
          return;
        }

        // Check the access token availability
        if (!this.accessToken) {
          return;
        }

        // Check the access token expire date
        // if (AuthUtils.isTokenExpired(this.accessToken)) {
        //   return;
        // }

        // If the access token exists and it didn't expire, sign in using it to refresh token
        this.signInUsingToken().subscribe();
      }, tokenTimeout);
    }
  }

  private clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('aryzaRoles');

    this._authenticated = false;

    this.accessTokenSubject.next(this.accessToken);
  }
}